import { useMutation, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import APIService from '../APIService';
import { type InvitePayload } from '../../models/Org';
import type Org from '../../models/Org';
import { environment } from '../../environments/environment';
import { useParams } from 'react-router-dom';
import type Affiliate from '../../models/Affiliate';
import { type OrgConnection } from '../../models/OrgConnection';

const useOrg = ({ orgPath }: { orgPath?: string }) => {
  return useQuery<Org>({
    queryKey: ['org', orgPath],
    queryFn: async ({ queryKey }) => {
      const org = await APIService.GET(`/${queryKey[0]}/url/${queryKey[1]}`);
      return org.data;
    },
    enabled: !!orgPath,
  });
};

const useCreateOrg = () => {
  return useMutation({
    mutationFn: async (payload: { name: string; url: string }) => {
      const res = await APIService.POST('/org', undefined, payload);
      return res.data;
    },
    onSuccess: (data) => {
      window.location.replace(`/${data.url}`);
    },
  });
};

const useValidateOrgUrl = () => {
  return useMutation({
    mutationFn: async (url: string) => {
      const res = await APIService.POST('/org/validurl', undefined, { url });
      return res.data;
    },
  });
};

const useCurrentOrg = () => {
  const { orgPath = '' } = useParams();
  return useOrg({ orgPath });
};

const useOrgConnections = (
  orgPath: string,
  enabled = true,
  onSuccessCallback?: (orgConnections: OrgConnection[]) => void,
) => {
  const { data: org } = useOrg({ orgPath });

  return useQuery({
    queryKey: ['orgConnections', orgPath],
    queryFn: async () => {
      const connections = (await APIService.GET(`/org/${org?._id}/connections`)) as { data: OrgConnection[] };
      if (connections && onSuccessCallback) onSuccessCallback(connections.data);
      return connections.data.map((connection) => ({
        ...connection,
        id: connection._id,
        name: connection?.metadata?.profile?.name,
        profileImg: connection?.metadata?.profile?.picture?.url,
      }));
    },
    enabled: !!org && enabled,
  });
};

const useCreateOrgConnections = (orgPath: string) => {
  const queryClient = useQueryClient();
  const { data: org } = useOrg({ orgPath });
  return useMutation({
    mutationFn: async (connection: any) => {
      const res = await APIService.POST(`/org/${org?._id}/connections`, undefined, connection);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgConnections'] });
      return { message: 'Org connection added successfully.' };
    },
  });
};

const useDeleteOrgConnections = (orgPath: string) => {
  const queryClient = useQueryClient();
  const { data: org } = useOrg({ orgPath });

  return useMutation({
    mutationFn: async (connectionId: string) => {
      const res = await APIService.DELETE(`/org/${org?._id}/connections/${connectionId}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgConnections'] });
      return { message: 'Org connection deleted successfully.' };
    },
    onError: (error) => {
      console.error('Mutation error:', error);
    },
  });
};

const useAddOrgCircleToken = (orgPath: string) => {
  const queryClient = useQueryClient();
  const { data: org } = useOrg({ orgPath });
  return useMutation({
    mutationFn: async ({
      connectionName,
      circleToken,
      apiVersion
    }: {
      connectionName: string;
      circleToken: string;
      apiVersion: string;
    }) => {
      const res = await APIService.POST(`/org/${org?._id}/connections/circle`, undefined, { connectionName, circleToken, apiVersion });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgConnections'] });
      return { message: 'Circle Access Token has been added successfully.' };
    },
  });
};

const useOrgBoards = (orgPath: string) => {
  const { data: org } = useOrg({ orgPath });
  return useQuery({
    queryKey: ['org', orgPath, 'boards'],
    queryFn: async () => {
      const boards = await APIService.GET(`/org/${org?._id}/boards`);
      return boards.data;
    },
    enabled: !!org,
  });
};

const useOrgMembers = (orgPath: string) => {
  const { data: org } = useOrg({ orgPath });
  return useQuery({
    queryKey: ['org', orgPath, 'members'],
    queryFn: async () => {
      const members = await APIService.GET(`/org/${org?._id}/members`);
      return members.data;
    },
    enabled: !!org,
  });
};

const useOrgInvite = (orgPath: string) => {
  const { data: org } = useOrg({ orgPath });
  return useMutation({
    mutationFn: async (payload: InvitePayload) => {
      const formattedPayload = {
        invitees: payload,
        userPoolId: environment.userPoolId,
      };
      const res = await APIService.POST(`/org/${org?._id}/invite`, undefined, { ...formattedPayload });
      return res.data;
    },
  });
};

const useOrgMemberRoleUpdate = (orgPath: string) => {
  const { data: org } = useOrg({ orgPath });
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { memberId: string; role: string }) => {
      const res = await APIService.POST(`/org/${org?._id}/members/${payload.memberId}/role`, undefined, { ...payload });
      return res.data;
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['org', orgPath, 'members'] });
    },
  });
};

const useRemoveOrgMember = (orgPath: string) => {
  const { data: org } = useOrg({ orgPath });
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (memberId: string) => {
      const res = await APIService.DELETE(`/org/${org?._id}/member/${memberId}`);
      return res.data;
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['org', orgPath, 'members'] });
    },
  });
};

const useSearchMembers = (orgPath: string) => {
  const { data: org } = useOrg({ orgPath });
  return useMutation({
    mutationFn: async (query: string) => {
      const res = await APIService.POST(`/org/${org?._id}/members/search`, undefined, { query });
      return res.data.map((member: any) => ({ value: member.email, label: member.name ?? member.email }));
    },
  });
};

const useSearchAffiliates = (orgId: string): UseMutationResult<Affiliate[]> => {
  return useMutation({
    mutationFn: async (query: string) => {
      const res = await APIService.POST(`/org/${orgId}/affiliates/search`, undefined, { query });
      return res.data;
    },
  });
};

const useOrgMeta = (orgPath: string) => {
  const { data: org } = useOrg({ orgPath });
  return org?.metadata;
};

export {
  useOrgConnections,
  useCreateOrgConnections,
  useDeleteOrgConnections,
  useAddOrgCircleToken,
  useOrgBoards,
  useOrgInvite,
  useOrgMembers,
  useOrgMemberRoleUpdate,
  useRemoveOrgMember,
  useSearchMembers,
  useCreateOrg,
  useValidateOrgUrl,
  useCurrentOrg,
  useSearchAffiliates,
  useOrgMeta,
};

export default useOrg;
